import { MGPMap, Set, Utils } from '@everyboard/lib';
import { BoardValue } from '../../jscaip/AI/BoardValue';
import { Coord } from '../../jscaip/Coord';
import { CoordSet } from '../../jscaip/CoordSet';
import { Orthogonal } from '../../jscaip/Orthogonal';
import { SandwichThreat } from '../../jscaip/PieceThreat';
import { Player } from '../../jscaip/Player';
import { TaflPawn } from './TaflPawn';
import { TaflPieceHeuristic } from './TaflPieceHeuristic';
export class TaflPieceAndInfluenceHeuristic extends TaflPieceHeuristic {
    getBoardValue(node, config) {
        const gameStatus = this.rules.getGameStatus(node, config);
        if (gameStatus.isEndGame) {
            return gameStatus.toBoardValue();
        }
        const state = node.gameState;
        const empty = TaflPawn.UNOCCUPIED;
        const pieceMap = this.getPiecesMap(state);
        const threatMap = this.getThreatMap(node, pieceMap);
        const filteredThreatMap = this.filterThreatMap(threatMap, state);
        let threatenedPiece = 0;
        let safePiece = 0;
        let totalInfluence = 0;
        for (const owner of Player.PLAYERS) {
            for (const coord of pieceMap.get(owner).get()) {
                if (filteredThreatMap.get(coord).isPresent()) {
                    threatenedPiece += owner.getScoreModifier();
                }
                else {
                    safePiece += owner.getScoreModifier();
                    let influence = 0;
                    for (const dir of Orthogonal.ORTHOGONALS) {
                        let testedCoord = coord.getNext(dir, 1);
                        while (state.hasPieceAt(testedCoord, empty)) {
                            influence++;
                            testedCoord = testedCoord.getNext(dir, 1);
                        }
                    }
                    totalInfluence += influence * owner.getScoreModifier();
                }
            }
        }
        return BoardValue.multiMetric([
            safePiece,
            threatenedPiece,
            totalInfluence,
        ]);
    }
    getPiecesMap(state) {
        const empty = TaflPawn.UNOCCUPIED;
        const zeroPieces = [];
        const onePieces = [];
        for (let y = 0; y < state.getHeight(); y++) {
            for (let x = 0; x < state.getWidth(); x++) {
                const coord = new Coord(x, y);
                const piece = state.getPieceAt(coord);
                if (piece !== empty) {
                    const owner = state.getAbsoluteOwner(coord);
                    if (owner === Player.ZERO) {
                        zeroPieces.push(coord);
                    }
                    else {
                        onePieces.push(coord);
                    }
                }
            }
        }
        const map = new MGPMap([
            { key: Player.ZERO, value: new CoordSet(zeroPieces) },
            { key: Player.ONE, value: new CoordSet(onePieces) },
        ]);
        return map;
    }
    getThreatMap(node, pieces) {
        const threatMap = new MGPMap();
        for (const player of Player.PLAYERS) {
            for (const piece of pieces.get(player).get()) {
                const threats = this.getThreats(piece, node.gameState);
                if (this.isThreatReal(piece, node.gameState, threats)) {
                    threatMap.set(piece, new Set(threats));
                }
            }
        }
        return threatMap;
    }
    getThreats(coord, state) {
        const owner = state.getAbsoluteOwner(coord);
        Utils.assert(owner.isPlayer(), 'TaflPieceAndInfluenceHeuristic.getThreats should be called with an occupied coordinate');
        const threatenerPlayer = owner.getOpponent();
        const threats = [];
        for (const dir of Orthogonal.ORTHOGONALS) {
            const directThreat = coord.getPrevious(dir, 1);
            if (this.isAThreat(directThreat, state, threatenerPlayer)) {
                const movingThreats = [];
                for (const captureDirection of Orthogonal.ORTHOGONALS) {
                    if (captureDirection === dir.getOpposite()) {
                        continue;
                    }
                    let futureCapturer = coord.getNext(dir, 1);
                    while (state.hasPieceAt(futureCapturer, TaflPawn.UNOCCUPIED)) {
                        futureCapturer = futureCapturer.getNext(captureDirection);
                    }
                    if (state.hasOwnerAt(futureCapturer, threatenerPlayer) &&
                        coord.getNext(dir, 1).equals(futureCapturer) === false) {
                        movingThreats.push(futureCapturer);
                    }
                }
                threats.push(new SandwichThreat(directThreat, new CoordSet(movingThreats)));
            }
        }
        return threats;
    }
    isAThreat(coord, state, opponent) {
        if (state.isNotOnBoard(coord)) {
            return false;
        }
        if (state.getAbsoluteOwner(coord) === opponent) {
            return true;
        }
        if (this.rules.isThrone(state, coord)) {
            if (opponent === Player.ONE) { // Defender
                return true;
            }
            else {
                return state.getPieceAt(coord) === TaflPawn.UNOCCUPIED;
            }
        }
        return false;
    }
    isThreatReal(coord, state, threats) {
        if (threats.length === 0) {
            return false;
        }
        if (state.getPieceAt(coord).isKing()) {
            return threats.length === 3;
        }
        else {
            for (const threat of threats) {
                if (threat.mover.size() > 0) {
                    return true;
                }
            }
            return false;
        }
    }
    filterThreatMap(threatMap, state) {
        const filteredThreatMap = new MGPMap();
        const threateneds = threatMap.getKeyList();
        const threatenedPlayerPieces = threateneds.filter((coord) => {
            return state.getAbsoluteOwner(coord) === state.getCurrentPlayer();
        });
        const threatenedOpponentPieces = new CoordSet(threateneds.filter((coord) => {
            return state.getAbsoluteOwner(coord) === state.getCurrentOpponent();
        }));
        for (const threatenedPiece of threatenedPlayerPieces) {
            const oldThreatSet = threatMap.get(threatenedPiece).get();
            const newThreatSet = [];
            for (const threat of oldThreatSet) {
                if (threatenedOpponentPieces.contains(threat.directThreat) === false) {
                    // if the direct threat of this piece is not a false threat
                    const newMover = [];
                    for (const mover of threat.mover) {
                        if (threatenedOpponentPieces.contains(mover) === false) {
                            // if the moving threat of this piece is real
                            newMover.push(mover);
                        }
                    }
                    if (newMover.length > 0) {
                        newThreatSet.push(new SandwichThreat(threat.directThreat, new CoordSet(newMover)));
                    }
                }
            }
            if (newThreatSet.length > 0) {
                filteredThreatMap.set(threatenedPiece, new Set(newThreatSet));
            }
        }
        for (const threatenedOpponentPiece of threatenedOpponentPieces) {
            const threatSet = threatMap.get(threatenedOpponentPiece).get();
            filteredThreatMap.set(threatenedOpponentPiece, threatSet);
        }
        return filteredThreatMap;
    }
}
